import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
    Flex,
    Button,
    Table,
    ConfigProvider,
    Space,
    Divider,
    Typography,
    Card,
} from '@app/ui';
import { useTranslation } from 'react-i18next';
import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import * as format from '@app/utils/formatters';
import shortCurrency from '@app/utils/shortCurrency';
import '@app/components/common/modals/PostModal.scss';
import { VerbalMentionRsp } from '@app/types/api';
import { findAllMentionsInContext } from '@app/broadcasts/utils/broadcastsUtils';

const domNode = document.getElementById('broadcast-details-modal');

interface VerbalMentionVideoModalProps {
    verbalMention: VerbalMentionRsp;
    currencyCode: string;
    onClose: () => void;
    onTrackVideoPlayed?: () => void;
    isDownloadingReport?: boolean;
    getReport?: () => void;
}

const VerbalMentionVideoModal = ({
    verbalMention,
    currencyCode,
    onClose,
    onTrackVideoPlayed,
    isDownloadingReport = false,
    getReport,
}: VerbalMentionVideoModalProps) => {
    const { t } = useTranslation('measurement');
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isVideoPlayingTracked, setIsVideoPlayingTracked] = useState(false);

    useEffect(() => {
        document.body.classList.add('-noscroll');
        return () => {
            document.body.classList.remove('-noscroll');
        };
    }, []);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.onplay = () => {
                if (!isVideoPlayingTracked) {
                    setIsVideoPlayingTracked(true);
                    onTrackVideoPlayed?.();
                }
            };
        }
    }, [videoRef.current, isVideoPlayingTracked]);

    // Get context substrings with mentions
    const contextSubstrings = findAllMentionsInContext(
        verbalMention.context ?? '',
        verbalMention.mention,
    );

    return ReactDOM.createPortal(
        <div
            className="modal-overlay modal-shown"
            style={{ zIndex: 10 }}
            onClick={onClose}
        >
            <div
                className="modal-v2-wrapper"
                style={{
                    maxWidth: 600,
                }}
                onClick={e => e.stopPropagation()}
            >
                <Button
                    className="button-close-v2"
                    color="primary"
                    variant="outlined"
                    icon={<CloseOutlined />}
                    onClick={onClose}
                    size={'middle'}
                />
                <Flex
                    className="modal-v2"
                    vertical
                    style={{
                        maxWidth: 600,
                    }}
                >
                    <video
                        src={verbalMention.videoUrl}
                        width="100%"
                        height="324px"
                        controls
                        ref={videoRef}
                    >
                        <p>
                            {t(
                                'broadcastExposure.detailsModal.videoNotSupported',
                            )}{' '}
                            <a href={verbalMention.videoUrl}>
                                {t(
                                    'broadcastExposure.detailsModal.linkToVideo',
                                )}
                            </a>{' '}
                            {t('broadcastExposure.detailsModal.instead')}
                        </p>
                    </video>
                    <Flex
                        className="modal-v2-actions"
                        justify="center"
                        align="center"
                    >
                        <Button
                            icon={<DownloadOutlined />}
                            loading={isDownloadingReport}
                            disabled={isDownloadingReport}
                            onClick={getReport}
                            size="small"
                            type="primary"
                            color="primary"
                            variant="outlined"
                        >
                            {isDownloadingReport
                                ? t('broadcastExposure.detailsModal.loading')
                                : t('broadcastExposure.detailsModal.download')}
                        </Button>
                        <Flex
                            className="modal-v2-caption-container"
                            style={{
                                padding: 0,
                                textAlign: 'center',
                                paddingLeft: 12,
                            }}
                            flex={1}
                            justify="flex-start"
                            align="baseline"
                        >
                            <Typography.Text
                                style={{
                                    fontWeight: 400,
                                }}
                            >
                                {verbalMention.broadcastName}
                            </Typography.Text>
                        </Flex>
                    </Flex>
                    <Flex style={{ width: '100%' }}>
                        <Card
                            type="inner"
                            size="small"
                            title={t('broadcastExposure.detailsModal.context')}
                            style={{
                                width: '100%',
                                borderColor: 'rgba(0, 0, 0, 0.15)',
                            }}
                            styles={{
                                header: {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    borderColor: 'rgba(0, 0, 0, 0.06)',
                                    textAlign: 'center',
                                },
                            }}
                        >
                            {verbalMention.context ? (
                                <Typography>
                                    <Typography.Paragraph>
                                        {contextSubstrings.map((e, i) => {
                                            if (e.isMention) {
                                                return (
                                                    <Typography.Text
                                                        key={`${i}-${e.substring}`}
                                                        mark
                                                    >
                                                        {e.substring}
                                                    </Typography.Text>
                                                );
                                            } else {
                                                return e.substring;
                                            }
                                        })}
                                    </Typography.Paragraph>
                                </Typography>
                            ) : (
                                <Typography.Text strong>
                                    {t(
                                        'broadcastExposure.detailsModal.noContextAvailable',
                                    )}
                                </Typography.Text>
                            )}
                        </Card>
                    </Flex>
                    <Flex className="modal-v2-stats-data">
                        <Flex className="modal-v2-stats-header-row">
                            <Space className="modal-v2-stats-header">
                                <Typography.Text strong>
                                    {t(
                                        'broadcastExposure.verbalMentions.modal.tableHeaders.rightsholder',
                                    )}
                                </Typography.Text>
                            </Space>
                            <Space className="modal-v2-stats-header">
                                <Typography.Text strong>
                                    {t(
                                        'broadcastExposure.verbalMentions.modal.tableHeaders.sponsor',
                                    )}
                                </Typography.Text>
                            </Space>
                            <Space className="modal-v2-stats-header">
                                <Typography.Text strong>
                                    {t(
                                        'broadcastExposure.verbalMentions.modal.tableHeaders.placement',
                                    )}
                                </Typography.Text>
                            </Space>
                        </Flex>
                        <Divider className="modal-v2-divider" />
                        <Flex className="modal-v2-stats-header-row">
                            <Space className="modal-v2-stats-body">
                                <Typography.Text style={{ fontWeight: 400 }}>
                                    {verbalMention.rightsholder.name}
                                </Typography.Text>
                            </Space>
                            <Space className="modal-v2-stats-body">
                                <Typography.Text style={{ fontWeight: 400 }}>
                                    {verbalMention.sponsor.name}
                                </Typography.Text>
                            </Space>
                            <Space className="modal-v2-stats-body">
                                <Typography.Text style={{ fontWeight: 400 }}>
                                    {verbalMention.placement.name}
                                </Typography.Text>
                            </Space>
                        </Flex>
                    </Flex>
                    <ConfigProvider
                        theme={{
                            components: {
                                Table: {
                                    headerBg: '#e1effa',
                                },
                            },
                        }}
                    >
                        <Table
                            size="small"
                            bordered
                            pagination={false}
                            rowHoverable={false}
                            style={{
                                width: '100%',
                            }}
                            dataSource={[
                                {
                                    key: 0,
                                    impressions:
                                        verbalMention.impressions === 0
                                            ? '-'
                                            : format.abbreviatedDecimal(
                                                  verbalMention.impressions,
                                              ),
                                    duration:
                                        verbalMention.duration === 0
                                            ? '-'
                                            : `${verbalMention.duration}s`,
                                    mvp:
                                        verbalMention.averageMvpScore === 0
                                            ? '-'
                                            : format.percentage(
                                                  verbalMention.averageMvpScore,
                                              ),
                                    smv:
                                        verbalMention.sponsorMediaValue === 0
                                            ? '-'
                                            : shortCurrency(
                                                  verbalMention.sponsorMediaValue,
                                                  1,
                                                  currencyCode,
                                              ),
                                    network: verbalMention.network.name,
                                },
                            ]}
                            columns={[
                                {
                                    title: t(
                                        'broadcastExposure.verbalMentions.modal.tableHeaders.impressions',
                                    ),
                                    dataIndex: 'impressions',
                                    width: '20%',
                                    align: 'center',
                                },
                                {
                                    title: t(
                                        'broadcastExposure.verbalMentions.modal.tableHeaders.duration',
                                    ),
                                    dataIndex: 'duration',
                                    width: '20%',
                                    align: 'center',
                                },
                                {
                                    title: t(
                                        'broadcastExposure.verbalMentions.modal.tableHeaders.mvp',
                                    ),
                                    dataIndex: 'mvp',
                                    width: '20%',
                                    align: 'center',
                                },
                                {
                                    title: t(
                                        'broadcastExposure.verbalMentions.modal.tableHeaders.smv',
                                    ),
                                    dataIndex: 'smv',
                                    width: '20%',
                                    align: 'center',
                                },
                                {
                                    title: t(
                                        'broadcastExposure.verbalMentions.modal.tableHeaders.network',
                                    ),
                                    dataIndex: 'network',
                                    width: '20%',
                                    align: 'center',
                                },
                            ]}
                        />
                    </ConfigProvider>
                </Flex>
            </div>
        </div>,
        domNode,
    );
};

export default VerbalMentionVideoModal;
